import React, { useCallback, useState } from "react";
import { Modal, Typography, Input, Checkbox, Button } from "antd";
import { useTranslation } from "react-i18next";
import axios, { AxiosError, AxiosResponse } from "axios";
import moment from "moment/moment";

import { BaseType } from "antd/es/typography/Base";
import api from "../../../services/api";
import { useToast } from "../../../hooks/toast";
import { IComments, IRequirement } from "../../Project";
import { Flex } from "./styled";
import { Card } from "../../../components/ui";
import FormItem from "../../../components/FormItem";
import { removeEmptyKeys } from "../../../utils/formatters";
import IStatusImpediment from "../../../interfaces/IStatusImpediment";

const { Text } = Typography;
const { TextArea } = Input;

interface IConcludeActivityModalProps {
  activity?: IRequirement;
  visible: boolean;
  editHistoricActivity?: boolean;
  handleClose: (activity?: IRequirement) => void;
  show_comments?: "comment_activity" | "comment_estimator";
}

const HistoricActivityModal: React.FC<IConcludeActivityModalProps> = ({
  activity,
  visible,
  editHistoricActivity,
  handleClose,
  show_comments = "comment_activity",
}) => {
  const { t } = useTranslation(["activities-actions"]);
  const [loading, setLoading] = useState<boolean>(false);
  const [historicActivity, setHistoricActivity] = useState<string | undefined>(
    undefined,
  );

  const { addToast } = useToast();

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined) => {
      let description;

      switch (response?.status) {
        case 404:
          description = "A atividade não foi encontrada";
          break;
        default:
          description = "Um erro comentar a atividade, tente novamente";
      }

      addToast({
        type: "error",
        title: "Erro ao concluir",
        description,
      });
    },
    [addToast],
  );

  const handleSubmit = useCallback(async () => {
    setLoading(true);

    try {
      const response = await api.patch(
        `/activities/${activity?.id}/factory`,
        removeEmptyKeys({
          comment_activity: historicActivity,
          project_id: activity?.project_id,
          impediment: !activity?.date_begin_activity || undefined,
        }),
      );

      handleClose(response.data);

      setHistoricActivity("");

      addToast({
        type: "success",
        title: "Comentário Adicionado",
        description: "O comentário foi adicionado com sucesso",
      });
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const serverError = err as AxiosError;
        generateErrorToast(serverError.response);
      } else {
        console.log(err);
      }
    }

    setLoading(false);
  }, [
    activity?.id,
    addToast,
    generateErrorToast,
    handleClose,
    historicActivity,
  ]);

  const borderColor = (comment: IComments): string => {
    if (comment.status === IStatusImpediment.warning) return "#ff0000";

    if (comment.status === IStatusImpediment.concluded) return "green";
    return "#f0f0f0";
  };

  const typeText = (status?: string): BaseType => {
    if (status === IStatusImpediment.concluded) return "success";
    if (status === IStatusImpediment.warning) return "danger";
    return "warning";
  };

  return (
    <Modal
      title={
        show_comments === "comment_activity"
          ? t(`Descrição/Histórico da Atividade`)
          : t(`Descrição da Etimativa da Atividade`)
      }
      visible={visible}
      onOk={handleSubmit}
      footer={editHistoricActivity}
      onCancel={() => handleClose()}
      destroyOnClose
      okButtonProps={{
        loading,
        disabled: !historicActivity || historicActivity?.length < 5,
      }}
      getContainer={false}
      bodyStyle={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Text style={{ marginBottom: 10 }}>
        {!editHistoricActivity &&
          `Descreva o que ocorreu durante a execução da atividade "${activity?.description}"`}
      </Text>
      {!editHistoricActivity ? (
        <>
          <FormItem>
            <Button disabled={!!activity?.date_begin_activity}>
              <Checkbox checked={!activity?.date_begin_activity}>
                Impedimento
              </Checkbox>
            </Button>
            <Button disabled={!activity?.date_begin_activity}>
              <Checkbox checked={!!activity?.date_begin_activity}>
                Desenvolvimento
              </Checkbox>
            </Button>
          </FormItem>
          <TextArea
            style={{ minHeight: 200 }}
            onChange={e => setHistoricActivity(e.target.value)}
          />
          <Text type="secondary">
            O histórico deve conter no mínimo 5 caracteres
          </Text>
        </>
      ) : (
        <>
          {activity?.comments
            ?.filter(c => c[show_comments])
            ?.map(comment => (
              <Flex key={comment.id} isBorder>
                <>
                  {" "}
                  <Text type={typeText(comment.status)}>
                    {comment.status ? "Impedimento" : "Desenvolvimento"}
                  </Text>
                  <Card
                    style={{
                      borderColor: borderColor(comment),
                    }}
                  >
                    <Flex>
                      <Text>{comment[show_comments]}</Text>
                      <Text type="secondary">
                        Atualização realizada por{" "}
                        <Text style={{ color: "orange" }}>
                          {show_comments === "comment_activity"
                            ? comment?.user?.name
                            : activity?.estimator?.name}
                        </Text>{" "}
                        em{" "}
                        {moment(comment.created_at).format(
                          "DD/MM/YYYY [as] HH:mm",
                        )}
                      </Text>
                    </Flex>
                  </Card>
                </>
              </Flex>
            ))}
        </>
      )}
    </Modal>
  );
};

export default HistoricActivityModal;
